import type { SelectedFilter } from "~/components/build/Show/components/Filter";
import PanelTitle from "~/components/build/Show/components/PanelTitle";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";
import { SCOPE_CATEGORY_KEY } from "~/components/build/Show/lib/annotationFilters";
import type { AnnotationScope } from "~/components/build/Show/lib/types";

interface AnnotationsCountProps {
  /**
   * The currently selected filters from the AnnotationsPanel. Taking the
   * generic filter list (rather than scopes only) means future filter
   * categories surface in the count automatically.
   */
  selectedFilters: SelectedFilter[];
}

/**
 * Renders the annotation count summary for the AnnotationsPanel header.
 *
 * Shows just "Annotations" when the build has no annotations, "Annotations {totalCount}"
 * when no scope filter is applied, and "Annotations {filteredCount} of {totalCount}"
 * whenever the user has selected one or more scopes — even if those scopes happen
 * to match every annotation (e.g. "10 of 10").
 */
export default function AnnotationsCount({ selectedFilters }: AnnotationsCountProps) {
  const { build } = useBuild();
  const annotationCounts = getAnnotationCounts(build);
  const hasAnnotations = annotationCounts.totalCount > 0;

  const selectedScopes = selectedFilters
    .filter((filter) => filter.categoryKey === SCOPE_CATEGORY_KEY)
    .map((filter) => filter.value as AnnotationScope);

  const hasScopeFilter = selectedScopes.length > 0;

  // With no scope filter applied, every annotation is visible. When one or more
  // scopes are selected (even if all of them are), sum the matching scope counts
  // so the header reflects the user's explicit selection.
  const filteredCount = hasScopeFilter
    ? selectedScopes.reduce((sum, scope) => sum + annotationCounts[scope], 0)
    : annotationCounts.totalCount;

  return (
    <PanelTitle
      testId="annotations-count"
      label="Annotations"
      count={hasAnnotations ? filteredCount : undefined}
      totalCount={hasAnnotations && hasScopeFilter ? annotationCounts.totalCount : undefined}
    />
  );
}
